import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SafetyDashboardPayload, TopSpeedVehicle, VehicleSafetyDetail } from '../../safety-dashboard.model';
import { SafetyDashboardStore } from '../../safety-dashboard.store';
import Chart from 'chart.js/auto';

interface SpeedRow {
  strFullName: string;
  strVehicleNo: string;
  Speed80Seconds: number;
  Speed100Seconds: number;
  Speed110Seconds: number;
  Speed120Seconds: number;
  speedTotal: number;
  MaxSpeed: number;
}

@Component({
  selector: 'app-safety-speed',
  templateUrl: './safety-speed.component.html',
  styleUrls: ['./safety-speed.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetySpeedComponent implements OnInit, OnDestroy {
  loading = false;
  hasData = false;
  topSpeedRows: SpeedRow[] = [];

  private sub = new Subscription();
  private charts: any[] = [];
  private payload: SafetyDashboardPayload | null = null;

  constructor(private store: SafetyDashboardStore, private cdr: ChangeDetectorRef) {
    Chart.defaults.font.family = "'vazir', Tahoma, sans-serif";
  }

  ngOnInit(): void {
    this.sub.add(this.store.loading$.subscribe((v) => { this.loading = v; this.cdr.markForCheck(); }));
    this.sub.add(this.store.payload$.subscribe((p) => { this.payload = p; this.refresh(); }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroyCharts();
  }

  trackByRow(_: number, r: SpeedRow): string {
    return r.strFullName + '|' + r.strVehicleNo;
  }

  toEn(n: number | null | undefined, fraction = 0): string {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: fraction });
  }

  private refresh(): void {
    const p = this.payload;
    this.hasData = !!p && (!!p.speedStatistics || p.vehicles.length > 0);
    if (!this.hasData || !p) {
      this.topSpeedRows = [];
      this.destroyCharts();
      this.cdr.markForCheck();
      return;
    }
    this.topSpeedRows = this.resolveTopSpeed(p);
    this.cdr.markForCheck();
    setTimeout(() => this.renderCharts(p), 60);
  }

  private resolveTopSpeed(p: SafetyDashboardPayload): SpeedRow[] {
    if (p.topSpeedVehicles.length) {
      return p.topSpeedVehicles.slice(0, 15).map((x) => toSpeedRow(x));
    }

    if (!p.vehicles.length) return [];
    return [...p.vehicles]
      .sort((a, b) => (Number(b.Speed120Seconds) || 0) - (Number(a.Speed120Seconds) || 0))
      .slice(0, 15)
      .map((x) => toSpeedRow(x));
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => { try { c.destroy(); } catch {} });
    this.charts = [];
  }

  private canvas(id: string): HTMLCanvasElement | null {
    return document.getElementById(id) as HTMLCanvasElement | null;
  }

  private barOptions(): any {
    return {
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
        x: { ticks: { color: '#b9c7d2', font: { size: 12 } }, grid: { display: false } },
      },
      plugins: { legend: { display: false } },
    };
  }

  private renderCharts(p: SafetyDashboardPayload): void {
    this.destroyCharts();
    if (!this.hasData) return;
    const s = p.speedStatistics;
    if (!s) return;

    const push = (id: string, labels: string[], data: number[], colors: string[]) => {
      const el = this.canvas(id);
      if (!el) return;
      this.charts.push(new Chart(el, {
        type: 'bar',
        data: { labels, datasets: [{ label: '', data, backgroundColor: colors, borderRadius: 8 }] },
        options: this.barOptions(),
      }));
    };

    push('speedDist', ['80-99', '100-109', '110-119', '120+'],
      [s.TotalSpeed80Seconds, s.TotalSpeed100Seconds, s.TotalSpeed110Seconds, s.TotalSpeed120Seconds],
      ['#35d07f', '#f5c451', '#ff8b4c', '#ff5d68']);
    push('speedAvg', ['80-99', '100-109', '110-119', '120+'],
      [s.AvgSpeed80Seconds, s.AvgSpeed100Seconds, s.AvgSpeed110Seconds, s.AvgSpeed120Seconds],
      ['#35d07f', '#f5c451', '#ff8b4c', '#ff5d68']);
    push('peakSamples', ['140+', '160+', '180+', '200+'],
      [s.PeakSpeedSamples140, s.PeakSpeedSamples160, s.PeakSpeedSamples180, s.PeakSpeedSamples200],
      ['#ff9b54', '#ff8b4c', '#ff5d68', '#c0392b']);
    push('peakDuration', ['160+', '180+'],
      [s.PeakSpeedDuration160, s.PeakSpeedDuration180],
      ['#ff5d68', '#c0392b']);
  }
}

function toSpeedRow(x: VehicleSafetyDetail | TopSpeedVehicle): SpeedRow {
  const s80 = Number(x.Speed80Seconds) || 0;
  const s100 = Number(x.Speed100Seconds) || 0;
  const s110 = Number(x.Speed110Seconds) || 0;
  const s120 = Number(x.Speed120Seconds) || 0;
  return {
    strFullName: x.strFullName ?? '',
    strVehicleNo: x.strVehicleNo ?? '',
    Speed80Seconds: s80,
    Speed100Seconds: s100,
    Speed110Seconds: s110,
    Speed120Seconds: s120,
    speedTotal: s80 + s100 + s110 + s120,
    MaxSpeed: Number(x.MaxSpeed) || 0,
  };
}
